import type { OcrParseResult, OcrWord } from "@haulsheet/core";
import { OcrFormat, parseOcrTsv, parseOcrWordPasses } from "@haulsheet/core";
import { ocrPrep } from "./prep.js";

const OCR_TIMEOUT_MS = 45_000;

export type OcrFailReason = "timeout" | "open-failed" | "empty";

function withTimeout<T>(promise: Promise<T>, ms: number, msg: string): Promise<T> {
  return new Promise((res, rej) => {
    let done = false;
    const t = setTimeout(() => {
      if (!done) {
        done = true;
        rej(new Error(msg));
      }
    }, ms);
    promise.then(
      (v) => {
        if (done) return;
        done = true;
        clearTimeout(t);
        res(v);
      },
      (e) => {
        if (done) return;
        done = true;
        clearTimeout(t);
        rej(e);
      },
    );
  });
}

function mapWords(words: Array<{ text: string; confidence?: number; bbox?: { x0: number; y0: number; x1: number; y1: number } }> | undefined): OcrWord[] {
  return (words ?? []).map((w) => ({
    text: w.text,
    conf: w.confidence ?? 0,
    x0: w.bbox?.x0 ?? 0,
    y0: w.bbox?.y0 ?? 0,
    x1: w.bbox?.x1 ?? 0,
    y1: w.bbox?.y1 ?? 0,
  }));
}

function failReason(err: unknown): OcrFailReason {
  const msg = err instanceof Error ? err.message : String(err ?? "");
  if (/timed?\s*out/i.test(msg)) return "timeout";
  if (/open|createWorker|prep|canvas|decode|image|blob|file/i.test(msg)) return "open-failed";
  return "open-failed";
}

function fuelFromParsed(parsed: OcrParseResult): unknown[] | undefined {
  const fuel = parsed.fields["fuel"];
  return Array.isArray(fuel) ? fuel : undefined;
}

/**
 * Two-pass OCR (PSM 6 then PSM 11) after ocrPrep, matching index.html.
 * Formats via parseOcrTsv / parseOcrWordPasses (ratecon → bme → detail → bennett → board;
 * fuel rides on fields.fuel / top-level fuel; fuel_board when loads empty).
 * On failure returns unknown parse plus reason so timeout ≠ empty.
 */
export async function runOcr(file: File | Blob): Promise<{
  words: OcrWord[];
  text: string;
  parsed: OcrParseResult;
  /** Fuel rows from parse (also on parsed.fields.fuel). */
  fuel?: unknown[];
  reason?: OcrFailReason;
}> {
  let worker: Awaited<ReturnType<typeof import("tesseract.js")["createWorker"]>> | null = null;
  try {
    const { createWorker } = await import("tesseract.js");
    const canvas = await ocrPrep(file);
    worker = await createWorker("eng");
    const work = (async () => {
      await worker!.setParameters({ tessedit_pageseg_mode: "6" as never });
      const first = await worker!.recognize(canvas, {}, { text: true, tsv: true } as never);
      await worker!.setParameters({ tessedit_pageseg_mode: "11" as never });
      const second = await worker!.recognize(canvas, {}, { text: true, tsv: true } as never);
      return { first, second };
    })();
    const { first, second } = await withTimeout(work, OCR_TIMEOUT_MS, "OCR timed out.");
    const firstData = first.data as { text?: string; tsv?: string; words?: Parameters<typeof mapWords>[0] };
    const secondData = second.data as { text?: string; tsv?: string; words?: Parameters<typeof mapWords>[0] };
    const firstTsv = firstData.tsv || "";
    const secondTsv = secondData.tsv || "";
    const firstWords = mapWords(firstData.words);
    const secondWords = mapWords(secondData.words);
    const text = String(firstData.text || "") + "\n" + String(secondData.text || "");
    const parsed =
      firstTsv || secondTsv
        ? parseOcrTsv(firstTsv, secondTsv)
        : parseOcrWordPasses(firstWords, secondWords);
    const fuel = fuelFromParsed(parsed);
    if (
      parsed.format === OcrFormat.unknown &&
      !firstWords.length &&
      !secondWords.length &&
      !String(text).trim()
    ) {
      return { words: [], text: "", parsed, reason: "empty" };
    }
    return {
      words: firstWords.concat(secondWords),
      text,
      parsed,
      ...(fuel ? { fuel } : {}),
    };
  } catch (err) {
    return {
      words: [],
      text: "",
      parsed: {
        format: OcrFormat.unknown,
        fields: {},
        confidence: 0,
      },
      reason: failReason(err),
    };
  } finally {
    if (worker) {
      try {
        await worker.terminate();
      } catch {
        /* ignore */
      }
    }
  }
}
